import React from "react";
import RffAddressEditingField from "../../components/finalForm/inputs/RffAddressEditingField";
import RffDateField from "../../components/finalForm/inputs/RffDateField";
import RffDollarField from "../../components/finalForm/inputs/RffDollarField";
import RffEmailField from "../../components/finalForm/inputs/RffEmailField";
import RffPhoneField from "../../components/finalForm/inputs/RffPhoneField";
import RffSelectField from "../../components/finalForm/inputs/RffSelectField";
import RffTextField from "../../components/finalForm/inputs/RffTextField";
import { selectOptionsLoanProductType } from "../../constants/selectOptions/selectOptionsLoanProductType";
import { selectOptionsLoanPurpose } from "../../constants/selectOptions/selectOptionsLoanPurpose";
import {
  CITIZENSHIP_OPTIONS,
  FICO_OPTIONS,
} from "../LoanApplicationFormScreen/applicationFormShared";

/**
 * One renderer per prefill field, keyed by the field name the target form uses.
 * The two link kinds pick from this by name (see SEND_LINK_CONFIGS), so a field
 * is defined once and the per-kind lists stay a plain, auditable set of strings.
 *
 * The key IS the wire name. `subjectPropertyAddress` is the one exception: the
 * form stores a list of property objects, so the screen collects a single
 * address here and reshapes it into `subjectProperties` at submit time.
 *
 * Sizing: the grid is 12 columns. Borrower fields run three to a row
 * (`size={4}`); the loan's four short fields run four to a row (`size={3}`), so
 * product / purpose / amount / date read as one set. Anything needing its own
 * row (an address picker with a manual-entry fallback under it) omits `size`.
 */
const PREFILL_FIELDS = {
  firstName: () => (
    <RffTextField name="firstName" label="First Name" size={4} />
  ),
  middleName: () => (
    <RffTextField name="middleName" label="Middle Name" size={4} />
  ),
  lastName: () => <RffTextField name="lastName" label="Last Name" size={4} />,
  nameSuffix: () => <RffTextField name="nameSuffix" label="Suffix" size={4} />,
  /**
   * The ONE required field on this screen, and the only one that is not really
   * "prefill" — it is the delivery address. The mint endpoint requires
   * `recipient_email`, 400s without it, and emails the link on send, so there is
   * no address-less mode to fall back to.
   *
   * Rendered in the "What are you sending?" section rather than under Borrower:
   * it belongs with the decision about what goes out, not with the optional
   * details. It still prefills the borrower's own email field on the form.
   */
  emailAddress: () => (
    <RffEmailField
      required
      name="emailAddress"
      label="Recipient Email Address"
      // label="Borrower's email *"
      // label="Where the link(s) will be emailed to"
      // size={4}
      helperText="The link(s) will be sent here and this email is prefilled on the borrower's form."
      // helperText="Also prefills the email on the borrower's form."
    />
  ),
  phone: () => <RffPhoneField name="phone" label="Phone" size={4} />,

  citizenshipStatus: () => (
    <RffSelectField
      name="citizenshipStatus"
      label="Citizenship"
      options={CITIZENSHIP_OPTIONS}
      size={4}
    />
  ),
  entityName: () => (
    <RffTextField name="entityName" label="Entity Name" size={4} />
  ),
  estimatedFICO: () => (
    <RffSelectField
      name="estimatedFICO"
      label="Estimated FICO range"
      options={FICO_OPTIONS}
      size={4}
      // helperText="A range only — never an actual score."
    />
  ),
  loanProductType: () => (
    <RffSelectField
      name="loanProductType"
      label="Product type"
      options={selectOptionsLoanProductType}
      size={3}
    />
  ),
  loanPurpose: () => (
    <RffSelectField
      name="loanPurpose"
      label="Loan purpose"
      options={selectOptionsLoanPurpose}
      size={3}
    />
  ),
  baseLoanAmount: () => (
    <RffDollarField name="baseLoanAmount" label="Loan amount" size={3} />
  ),
  expectedClosingDate: () => (
    <RffDateField
      name="expectedClosingDate"
      label="Expected closing date"
      size={3}
    />
  ),
  subjectPropertyAddress: () => (
    <RffAddressEditingField
      name="subjectPropertyAddress"
      label="Subject property address"
    />
  ),
};

export const renderPrefillFields = (names = []) =>
  names.map((name) => {
    const render = PREFILL_FIELDS[name];
    if (!render) {
      console.error(`No prefill field renderer for "${name}"`);
      return null;
    }
    return <React.Fragment key={name}>{render()}</React.Fragment>;
  });

export default PREFILL_FIELDS;
